import { Client, Conversation } from "@twilio/conversations";
import { SyncClient, SyncMap } from "twilio-sync";
import { Dispatch } from "redux";
import log from "loglevel";

import { initMessagesListener } from "./listeners/messagesListener";
import { initParticipantsListener } from "./listeners/participantsListener";
import { initConversationListener } from "./listeners/conversationListener";
import { AppState, ConfigState, EngagementPhase, GlanceConfiguration } from "../definitions";
import { initClientListeners } from "./listeners/clientListener";
import { notifications } from "../../notifications";
import { ACTION_START_SESSION, ACTION_LOAD_CONFIG } from "./actionTypes";
import { addNotification, changeEngagementPhase, changeExpandedStatus } from "./genericActions";
import { MESSAGES_LOAD_COUNT } from "../../constants";
import { initializeSyncMapItem } from "../../utils/initializeSyncMapItem";
import { initSyncListeners } from "./listeners/syncListener";
import { PresenceVisitor } from "../../types/Glance";
import { initGlanceListeners } from "./listeners/glanceListener";
import { logLevel } from "../../logger";
import { sessionDataHandler } from "../../sessionDataHandler";

export function initConfig(config: ConfigState) {
    return {
        type: ACTION_LOAD_CONFIG,
        payload: config
    };
}

async function _initSession({
    token,
    conversationSid,
    glanceConfiguration,
    dispatch,
    getState
}: {
    token: string;
    conversationSid: string;
    glanceConfiguration?: GlanceConfiguration;
    dispatch: Dispatch;
    getState: () => AppState;
}) {
    let conversationsClient: Client;
    let glanceVisitor: PresenceVisitor | undefined;
    let syncMap: SyncMap;
    let conversation: Conversation;
    let participants;
    let users;
    let messages;

    const { config } = getState();

    try {
        conversationsClient = await Client.create(token, {
            logLevel
        });
        try {
            conversation = await conversationsClient.getConversationBySid(conversationSid);
        } catch (e) {
            dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
            return;
        }

        participants = await conversation.getParticipants();
        users = await Promise.all(participants.map(async (p) => p.getUser()));
        messages = (await conversation.getMessages(MESSAGES_LOAD_COUNT)).items;
    } catch (e) {
        log.error("Something went wrong when initializing session", e);
        throw e;
    }

    try {
        const syncClient = new SyncClient(token);
        const output = await initializeSyncMapItem({
            client: syncClient,
            conversationSid: conversation.sid
        });

        syncMap = output.map;
    } catch (e) {
        log.error("Something went wrong when initializing sync", e);
        throw e;
    }

    if (glanceConfiguration) {
        try {
            glanceVisitor = new window.GLANCE.Presence.Visitor({
                groupid: glanceConfiguration.groupid,
                visitorid: conversation.sid
            });

            initGlanceListeners(glanceVisitor, dispatch);

            glanceVisitor.presence();

            if (glanceVisitor.isconnected() === false) {
                log.info("initActions: Connecting the glance visitor as is not connected");
                glanceVisitor.connect();
            } else {
                log.info("initActions: Skipping connection on glanceVisitor as is already connected");
            }

            (window as unknown as { glanceVisitor: PresenceVisitor }).glanceVisitor = glanceVisitor;
        } catch (e) {
            log.error(`Something went wrong when intializing glance`, e);
        }
    }

    let currentPhase: EngagementPhase;

    if (conversation.state?.current === "closed") {
        if (config.disablePostChatSurvey) {
            currentPhase = EngagementPhase.ThankYouPhase;
        } else {
            currentPhase = EngagementPhase.PostChatSurvey;
        }
    } else {
        currentPhase = EngagementPhase.MessagingCanvas;
    }

    dispatch({
        type: ACTION_START_SESSION,
        payload: {
            token,
            conversationSid,
            conversationsClient,
            conversation,
            users,
            participants,
            messages,
            conversationState: conversation.state?.current,
            currentPhase,
            glanceVisitor,
            syncMap
        }
    });

    initClientListeners(conversationsClient, dispatch);
    initSyncListeners(syncMap, conversation.sid, dispatch);
    initConversationListener(conversation, dispatch, getState);
    initMessagesListener(conversation, dispatch);
    initParticipantsListener(conversation, dispatch);
}

export function autoInitSession({
    name,
    email,
    query,
    data,
    glanceConfiguration
}: {
    name: string;
    email: string;
    query: string;
    data: Record<string, string>;
    glanceConfiguration?: GlanceConfiguration;
}) {
    return async (dispatch: Dispatch, getState: () => AppState) => {
        // Expand to show the loading indicator
        dispatch(changeExpandedStatus({ expanded: true }));

        const response = await sessionDataHandler.fetchAndStoreNewSession({
            friendlyName: name,
            query,
            formData: {
                friendlyName: name,
                email,
                query,
                ...data
            }
        });

        return _initSession({
            token: response.token,
            conversationSid: response.conversationSid,
            glanceConfiguration,
            dispatch,
            getState
        });
    };
}

export function initSession({
    token,
    conversationSid,
    glanceConfiguration
}: {
    token: string;
    conversationSid: string;
    glanceConfiguration?: GlanceConfiguration;
}) {
    return async (dispatch: Dispatch, getState: () => AppState) => {
        return _initSession({
            token,
            conversationSid,
            glanceConfiguration,
            dispatch,
            getState
        });
    };
}
